// 編輯頁的機關時間軸「能拖」的接線：把 MechanismTimeline 的 edit 介面（lib/mechanisms/timelineEdit.ts）接到編輯頁的存檔和復原。
// 導演頁調整舞台的做法是「機關時間進草稿、cue 層級放開就存」；編輯頁沒有那份草稿，所以：
//   - 拖色塊 / key / 範圍條（機關的延遲、走幾秒）：整批一個交易存進後台（mech.commitTimings）；Cue 庫專案跟機關分頁一樣，進「還沒存的改動」
//   - 拖 cue 編號 / 拉轉場條 / 拖歌曲長度：放開就存（Cue 庫專案走導演頁同一組 stage-* 路由；舊專案走編輯頁原本的 updateCue）
//   - 每一個操作都記進編輯頁的復原紀錄（Ctrl+Z / Ctrl+Shift+Z），一次整批拖算一步；失敗就退回並跳白話錯誤
// 選取：時間軸上選的機關（可以多選）= 右邊面板選的那個機關（mech.focusedId）同步。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api';
import type { Cue } from '../api';
import { patchSongStage, setSlotStageCrossfade } from '../adjust/stageApi';
import { saveCueMoves } from '../adjust/cueMoveSave';
import { EditHistory, historyScope } from '../editHistory';
import { sortedSlotIds } from '../songSlots';
import { friendlyError } from '../songReview';
import { toast } from '../toast';
import type { PickHow, TimelineEdit, TimingChange } from './timelineEdit';
import type { EditorMechanisms } from './useMechanisms';

export interface EditorTimelineEditOptions {
  /** false（只能看的人）→ 不給 edit，時間軸只能看 */
  enabled: boolean;
  projectId: string | undefined;
  songId: string | null;
  /** Cue 庫專案：cue 層級的改動走 stage-* 路由（跟導演頁同一份） */
  libMode: boolean;
  cues: Cue[];
  selectedCueId: string | null;
  /** 舞台在預覽 Cue 庫的 cue（這首歌沒有 cue 指到它）時不能拖色塊：先切回真正的 cue */
  previewing: boolean;
  mech: Pick<EditorMechanisms, 'commitTimings' | 'focusedId' | 'setFocusedId'>;
  length: { seconds: number | null; source: 'video' | 'music' | 'custom' | 'auto'; editable: boolean };
  /** 這首歌自己設的長度（復原用） */
  customLengthSec: number | null;
  history: EditHistory;
  bumpHistory: () => void;
  /** 存完（或退回完）重讀這首歌的 cue、歌、機關位置；要等讀完 */
  reload: () => Promise<void>;
  /** 開始拖：結束時間預覽 */
  onGestureStart: () => void;
}

/** 已經跳過錯誤了，不用再跳一次 */
class QuietFail extends Error {}

interface Step { undo: () => Promise<void>; redo: () => Promise<void> }

/** 時間軸選取的規則：跟調整舞台的選取（AdjustStore）一樣的幾種方式 */
export function applyPick(cur: readonly string[], ids: readonly string[], how: PickHow, order?: readonly string[], anchor?: string | null): string[] {
  if (how === 'replace') return [...ids];
  if (how === 'add') return [...cur, ...ids.filter(i => !cur.includes(i))];
  if (how === 'remove') return cur.filter(i => !ids.includes(i));
  if (how === 'toggle') {
    const next = [...cur];
    for (const id of ids) { const at = next.indexOf(id); if (at >= 0) next.splice(at, 1); else next.push(id); }
    return next;
  }
  // range：從上一次點的到這一個（照畫面順序）
  const to = ids[0];
  const list = order ?? [];
  const a = anchor ? list.indexOf(anchor) : -1, b = to ? list.indexOf(to) : -1;
  if (!to || b < 0 || a < 0) return to ? [to] : [];
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return list.slice(lo, hi + 1);
}

export function useEditorTimelineEdit(o: EditorTimelineEditOptions): TimelineEdit | null {
  const latest = useRef(o);
  latest.current = o;
  const busyRef = useRef(false);

  // ── 選取 ──
  const [picked, setPicked] = useState<string[]>(() => (o.mech.focusedId ? [o.mech.focusedId] : []));
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  const anchorRef = useRef<string | null>(o.mech.focusedId);
  const { focusedId, setFocusedId } = o.mech;
  // 右邊面板選了別的機關 → 時間軸跟著選它
  useEffect(() => {
    if (focusedId && !pickedRef.current.includes(focusedId)) { setPicked([focusedId]); anchorRef.current = focusedId; }
  }, [focusedId]);
  const onPick = useCallback((ids: readonly string[], how: PickHow, order?: readonly string[]) => {
    const next = applyPick(pickedRef.current, ids, how, order, anchorRef.current);
    pickedRef.current = next;
    setPicked(next);
    if (how !== 'remove' && ids[0]) anchorRef.current = ids[0];
    const focus = next.length ? next[next.length - 1] : null;
    if (focus && focus !== latest.current.mech.focusedId) setFocusedId(focus);
  }, [setFocusedId]);

  // ── 機關的延遲 / 走幾秒 ──
  const onTimings = useCallback((changes: TimingChange[]) => {
    const cueId = latest.current.selectedCueId;
    if (!cueId || changes.length === 0) return;
    void latest.current.mech.commitTimings(cueId, changes);
  }, []);

  // ── cue 層級：一次只做一件事；做完重讀；記一步復原 ──
  const run = useCallback(async (label: string, fn: () => Promise<Step | null>): Promise<boolean> => {
    if (busyRef.current) return false;
    busyRef.current = true;
    const scope = historyScope(latest.current.selectedCueId);
    try {
      const step = await fn();
      await latest.current.reload();
      if (step) {
        latest.current.history.push(scope, {
          undo: async () => { await step.undo(); await latest.current.reload(); },
          redo: async () => { await step.redo(); await latest.current.reload(); },
        });
        latest.current.bumpHistory();
      }
      return true;
    } catch (e) {
      if (!(e instanceof QuietFail)) toast.error(`${label}沒有成功：${friendlyError(e)}`);
      try { await latest.current.reload(); } catch { /* 下次自動更新再讀 */ }
      return false;
    } finally {
      busyRef.current = false;
    }
  }, []);

  const onMoveCues = useCallback((changes: { cueId: string; videoTimeSec: number; crossfadeSeconds?: number }[]): Promise<boolean> => run('改 cue 的時間', async () => {
    const L = latest.current;
    const pid = L.projectId, sid = L.songId;
    if (!pid || !sid || changes.length === 0) return null;
    if (L.libMode) {
      // 跟導演頁同一組路由：先存被夾住的 cue 的轉場（色塊跟著走），再存時間（後台照時間重排編號）；失敗退回轉場
      return await saveCueMoves(pid, sid, changes);
    }
    // 舊專案：一個一個存時間，再照時間重排編號（跟「現在照時間排」同一個規則）
    const masters = L.cues.filter(c => c.status === 'master').sort((a, b) => a.order - b.order);
    const beforeIds = masters.map(c => c.id);
    const beforeTimes = new Map(masters.map(c => [c.id, c.videoTimeSec]));
    const beforeCross = new Map(masters.map(c => [c.id, c.crossfadeSeconds]));
    /** 時間和（被夾住時的）轉場秒數一起存 */
    const body = (c: { videoTimeSec: number; crossfadeSeconds?: number }) => ({ videoTimeSec: c.videoTimeSec, ...(c.crossfadeSeconds === undefined ? {} : { crossfadeSeconds: c.crossfadeSeconds }) });
    const bodyBack = (id: string) => {
      const cs = changes.find(c => c.cueId === id)?.crossfadeSeconds;
      const prevCross = beforeCross.get(id);
      return { videoTimeSec: beforeTimes.get(id) ?? null, ...(cs !== undefined && typeof prevCross === 'number' ? { crossfadeSeconds: prevCross } : {}) };
    };
    const newTimes = new Map(changes.map(c => [c.cueId, c.videoTimeSec]));
    const afterIds = sortedSlotIds(masters.map(c => ({ id: c.id, order: c.order, videoTimeSec: newTimes.has(c.id) ? newTimes.get(c.id)! : c.videoTimeSec })));
    const applied: string[] = [];
    try {
      for (const c of changes) { await api.updateCue(pid, sid, c.cueId, body(c)); applied.push(c.cueId); }
      await api.reorderCues(pid, sid, afterIds);
    } catch (e) {
      for (const id of applied) { try { await api.updateCue(pid, sid, id, bodyBack(id)); } catch { /* 退不回去就交給重讀 */ } }
      throw e;
    }
    return {
      undo: async () => {
        for (const c of changes) await api.updateCue(pid, sid, c.cueId, bodyBack(c.cueId));
        await api.reorderCues(pid, sid, beforeIds);
      },
      redo: async () => {
        for (const c of changes) await api.updateCue(pid, sid, c.cueId, body(c));
        await api.reorderCues(pid, sid, afterIds);
      },
    };
  }), [run]);

  const onTransition = useCallback((cueId: string, seconds: number, scale: TimingChange[]): Promise<boolean> => run('改轉場秒數', async () => {
    const L = latest.current;
    const pid = L.projectId, sid = L.songId;
    if (!pid || !sid) return null;
    let cross: Step;
    if (L.libMode) {
      const r = await setSlotStageCrossfade(pid, sid, cueId, seconds, { own: true });
      const prev = r.previous;
      cross = {
        undo: async () => { if (prev) await setSlotStageCrossfade(pid, sid, cueId, prev.crossfadeSeconds, { own: prev.own }); },
        redo: async () => { await setSlotStageCrossfade(pid, sid, cueId, seconds, { own: true }); },
      };
    } else {
      const before = L.cues.find(c => c.id === cueId)?.crossfadeSeconds;
      await api.updateCue(pid, sid, cueId, { crossfadeSeconds: seconds });
      cross = {
        undo: async () => { if (typeof before === 'number') await api.updateCue(pid, sid, cueId, { crossfadeSeconds: before }); },
        redo: async () => { await api.updateCue(pid, sid, cueId, { crossfadeSeconds: seconds }); },
      };
    }
    // 這個 cue 自己設了時間的機關跟著比例縮放，跟轉場秒數合成一步復原
    let scaleStep: Step | null = null;
    if (scale.length) {
      const ok = await L.mech.commitTimings(cueId, scale, { collect: s => { scaleStep = s; } });
      if (!ok) { try { await cross.undo(); } catch { /* 交給重讀 */ } throw new QuietFail(); }
    }
    const sc = scaleStep as Step | null;
    return {
      undo: async () => { if (sc) await sc.undo(); await cross.undo(); },
      redo: async () => { await cross.redo(); if (sc) await sc.redo(); },
    };
  }), [run]);

  const onSetLength = useCallback((sec: number): Promise<boolean> => run('改歌曲長度', async () => {
    const L = latest.current;
    const pid = L.projectId, sid = L.songId;
    if (!pid || !sid) return null;
    const prev = L.customLengthSec;
    await patchSongStage(pid, sid, { lengthSec: sec });
    return {
      undo: async () => { await patchSongStage(pid, sid, { lengthSec: prev }); },
      redo: async () => { await patchSongStage(pid, sid, { lengthSec: sec }); },
    };
  }), [run]);

  const onGestureStart = useCallback(() => { latest.current.onGestureStart(); }, []);

  const { enabled, selectedCueId, previewing, length } = o;
  return useMemo<TimelineEdit | null>(() => {
    if (!enabled) return null;
    return {
      editableCueId: previewing ? null : selectedCueId,
      pickedMechIds: picked,
      onPick,
      onTimings,
      cue: { canEdit: true, onMoveCues, onTransition },
      length: { seconds: length.seconds, source: length.source, editable: length.editable, onSet: onSetLength },
      onGestureStart,
    };
  }, [enabled, selectedCueId, previewing, picked, onPick, onTimings, onMoveCues, onTransition, length.seconds, length.source, length.editable, onSetLength, onGestureStart]);
}
